/**
 * BHL infinite scroll for the collection and search product grids (2026-10-14, request from Nathan).
 *
 * Progressive enhancement of the paginated grid rendered by snippets/product-grid.liquid. The server HTML keeps
 * Horizon's numbered pagination (real ?page=N links, so crawlers still reach every product) and adds a "Load more
 * products" link to the next page. Without this script those links work as they always have. With it:
 *   - as the shopper nears the end of the grid (IntersectionObserver, ROOT_MARGIN ahead, on the theme's scroll
 *     container), the next page is fetched with the Section Rendering API (the current URL, so sort, filters and q
 *     are kept, plus page=N and section_id) and its product cards are appended;
 *   - the first `data-auto-pages` loads (default 2) happen on scroll; after that each page needs a click on "Load
 *     more products", so the footer (phone, contact, trade information) stays reachable and data use is bounded;
 *   - at most `data-max-pages` pages (default 8) stay in the page; beyond that the button is a plain link that
 *     continues on the next page (a fresh document), which caps DOM growth;
 *   - the URL follows the last loaded page that rendered products, with history.replaceState (no extra history
 *     entries);
 *   - Back to the list restores the pages and the position: appended pages are kept in sessionStorage keyed by
 *     URL (within HTML_BUDGET; anything missing is fetched again) and the card that was at the top of the view
 *     is put back where it was;
 *   - each load is announced in an aria-live region; a keyboard user who asked for more lands on the first new card;
 *   - a filter or sort change aborts this feature's request in flight (an answer from Horizon's own prefetch is
 *     ignored), then nothing loads until the latest change's render has settled and the grid on screen is the
 *     server's grid for the current URL; otherwise (10 s, a failed or late render) the control becomes a plain link,
 *     because the cards on screen no longer match the URL;
 *   - pages are counted by the range consumed, so a page whose products are all already shown still moves on;
 *   - without the script, "Load more products" and "Show earlier products" are plain links beside the numbered ones;
 *   - on collection pages (the theme tracks collection views there) a PageViewEvent is dispatched per loaded page;
 *     search pages dispatch nothing extra.
 * The controller holds every decision and talks to the page through a small view, so it runs under node in the tests.
 */
import { sectionRenderer } from '@theme/section-renderer';
import { getIntersectionRoot, getScrollTop, scrollTo, scrollContainerMediaQuery } from '@theme/scroll-container';
import { PageViewEvent, StandardEvents } from '@shopify/events';

export const STORAGE_KEY = 'bhl-infinite-scroll:v1';
export const MAX_ENTRIES = 3;
export const HTML_BUDGET = 1500000; // characters of stored card HTML across every entry
export const MAX_AGE_MS = 30 * 60 * 1000;
export const MAX_FAILURES = 2; // after this many failed loads in a row the button is a plain link to the next page
export const ROOT_MARGIN = '0px 0px 600px 0px';
export const RESET_TIMEOUT_MS = 10000;

/**
 * The listing URL for a page: hash and section_id dropped; page 1 has no page parameter unless it is for a fetch.
 * @param {string} href
 * @param {number} page
 * @param {boolean} [forFetch]
 */
export function pageUrl(href, page, forFetch = false) {
  const url = new URL(href);
  url.hash = '';
  url.searchParams.delete('section_id');
  if (page > 1 || forFetch) url.searchParams.set('page', String(page));
  else url.searchParams.delete('page');
  return url.toString();
}

/** The same listing whatever the page: the key that lets a new entry replace the older one for that listing. */
export function listingKey(href) {
  return pageUrl(href, 1);
}

/** Back-navigation entries in sessionStorage. Every access is guarded; a failure only means no restore. */
export class SessionStore {
  /** @param {Storage | null | undefined} backend */
  constructor(backend) {
    this.backend = backend;
  }

  read() {
    try {
      const raw = this.backend?.getItem(STORAGE_KEY);
      const data = raw ? JSON.parse(raw) : null;
      return data && Array.isArray(data.entries) ? data.entries : [];
    } catch {
      return [];
    }
  }

  /** @param {any[]} entries newest first */
  write(entries) {
    let used = 0;
    for (const entry of entries) {
      const pages = entry.pages || {};
      for (const page of Object.keys(pages).sort((a, b) => Number(b) - Number(a))) {
        const size = String(pages[page]).length;
        if (used + size > HTML_BUDGET) delete pages[page];
        else used += size;
      }
      entry.pages = pages;
    }
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        this.backend?.setItem(STORAGE_KEY, JSON.stringify({ v: 1, entries }));
        return true;
      } catch {
        for (const entry of entries) entry.pages = {}; // over quota: keep the positions, pages come from the network
      }
    }
    return false;
  }

  find(href, section, now) {
    return this.read().find((e) => e.href === href && e.section === section && now - e.at <= MAX_AGE_MS) || null;
  }

  put(entry) {
    const rest = this.read().filter((e) => !(e.section === entry.section && e.listing === entry.listing));
    return this.write([entry, ...rest].slice(0, MAX_ENTRIES));
  }

  remove(listing, section) {
    const all = this.read();
    const rest = all.filter((e) => !(e.section === section && e.listing === listing));
    if (rest.length !== all.length) this.write(rest);
  }
}

/**
 * Every decision about loading, the URL, the stored position and what the region shows.
 * Options: view, fetchHtml(page, signal, fresh) -> Promise<string>, store, section, autoPages, maxPages, href(),
 * replaceHref(url), track(url) | null, now(), backForward.
 *
 * Pages are counted by the page range consumed (`range`), not by the cards on screen: a page whose products are all
 * already shown (the catalogue moved) or that has no product cards (search: pages and articles only) still moves the
 * range on, so the next request is always the next page.
 */
export class InfiniteController {
  constructor(o) {
    this.o = o;
    this.busy = null;
    this.autoUsed = 0;
    this.failures = 0;
    this.generation = 0;
    /**
     * Pages consumed so far, read from the cards when null. `shown` is the last page that rendered a product grid:
     * the URL and the Back entry follow it, because a search page holding only pages and articles renders no grid
     * (Back to it could not restore anything).
     * @type {{ first: number, last: number, shown: number } | null}
     */
    this.range = null;
    /** @type {number | null} the filter or sort change whose render is awaited */
    this.pendingReset = null;
    this.resetToken = 0;
    /** true when a filter or sort render never arrived: the cards on screen no longer match the URL */
    this.stale = false;
    /** @type {Map<number, string>} section HTML fetched during this page view, by page */
    this.html = new Map();
  }

  span() {
    return this.o.view.span();
  }

  bounds() {
    if (!this.range) {
      const s = this.span();
      this.range = s ? { first: s.first, last: s.last, shown: s.last } : null;
    }
    return this.range;
  }

  lastPage() {
    return this.o.view.lastPage();
  }

  blocked() {
    return this.pendingReset !== null || this.stale;
  }

  nextPage() {
    const b = this.bounds();
    return b && b.last < this.lastPage() ? b.last + 1 : null;
  }

  prevPage() {
    const b = this.bounds();
    return b && b.first > 1 ? b.first - 1 : null;
  }

  capped() {
    const b = this.bounds();
    return !!b && b.last - b.first + 1 >= this.o.maxPages;
  }

  /** Whether a click on the next or previous control is handled here; otherwise the link navigates as normal. */
  intercepts(dir) {
    if (this.stale) return false;
    const page = dir === 'next' ? this.nextPage() : this.prevPage();
    return page !== null && !this.capped() && this.failures < MAX_FAILURES;
  }

  wantsAuto() {
    return (
      !this.busy &&
      !this.blocked() &&
      this.failures === 0 &&
      this.autoUsed < this.o.autoPages &&
      this.nextPage() !== null &&
      !this.capped()
    );
  }

  /**
   * @param {'next' | 'prev'} dir
   * @param {'auto' | 'click'} trigger
   */
  async load(dir, trigger) {
    if (this.busy || this.blocked()) return 'busy';
    const page = dir === 'next' ? this.nextPage() : this.prevPage();
    if (page === null) {
      this.refresh();
      return 'none';
    }
    if (this.capped() || this.failures >= MAX_FAILURES) {
      this.refresh();
      return 'link';
    }
    if (trigger === 'auto') {
      if (!this.wantsAuto()) return 'skip';
      this.autoUsed++;
    }
    const generation = this.generation;
    const op = { dir, page, controller: new AbortController() };
    this.busy = op;
    this.refresh();
    let html;
    let parsed;
    try {
      // After a failure the shared renderer's cached answer for that URL may be the failed one: fetch afresh.
      html = this.html.get(page) ?? (await this.o.fetchHtml(page, op.controller.signal, this.failures > 0));
      if (generation !== this.generation || this.busy !== op) return 'stale';
      parsed = this.o.view.parse(html);
      if (!parsed) throw new Error('the response holds no grid section');
    } catch {
      if (generation !== this.generation || this.busy !== op) return 'stale';
      this.busy = null;
      this.failures++;
      this.refresh();
      this.o.view.announce('error', {});
      return 'error';
    }
    this.busy = null;
    this.failures = 0;
    const b = this.bounds();
    if (dir === 'next' && !parsed.hasGrid && !parsed.others.length) {
      // Past the end (the catalogue shrank since the first page): the list ends with the pages already shown.
      this.o.view.setLastPage(b.last);
      this.refresh();
      return 'end';
    }
    this.html.set(page, html);
    const focusAfter = trigger === 'click' || this.o.view.actionFocused(dir);
    if (parsed.lastPage) this.o.view.setLastPage(Math.max(parsed.lastPage, page));
    const added = dir === 'next' ? this.o.view.append(parsed, page) : this.o.view.prepend(parsed, page, true);
    if (dir === 'next') {
      b.last = page;
      if (parsed.hasGrid) b.shown = page;
    } else b.first = page;
    this.afterChange(null);
    this.o.track?.(pageUrl(this.o.href(), page));
    this.o.view.announce('loaded', { page, count: added.count });
    this.refresh();
    if (focusAfter && added.first) this.o.view.focusCard(added.first);
    else if (focusAfter) this.o.view.focusStatus(dir);
    if (dir === 'next') this.o.view.recheck();
    return 'loaded';
  }

  /** The URL follows the last page consumed, and the stored entry follows the page. */
  afterChange(anchor) {
    const b = this.bounds();
    if (!b) return;
    const url = pageUrl(this.o.href(), b.shown);
    if (url !== pageUrl(this.o.href(), this.pageInHref())) this.o.replaceHref(url);
    this.save(anchor);
  }

  pageInHref() {
    const value = Number(new URL(this.o.href()).searchParams.get('page'));
    return Number.isInteger(value) && value > 1 ? value : 1;
  }

  /** @param {{ id: string, top: number } | null} anchor */
  save(anchor) {
    const b = this.bounds();
    if (!b || this.stale || this.pendingReset !== null) return;
    const href = this.o.href();
    const listing = listingKey(href);
    if (b.first >= b.shown) {
      this.o.store.remove(listing, this.o.section);
      return;
    }
    const previous = this.o.store.find(pageUrl(href, b.shown), this.o.section, this.o.now());
    const pages = {};
    for (let p = b.first; p < b.shown; p++) {
      if (this.html.has(p)) pages[p] = this.html.get(p);
    }
    this.o.store.put({
      href: pageUrl(href, b.shown),
      listing,
      section: this.o.section,
      start: b.first,
      through: b.shown,
      pages,
      anchor: anchor ?? previous?.anchor ?? null,
      at: this.o.now(),
    });
  }

  /** Back to the list: put back the pages before the one the server rendered, then the position. */
  async restore() {
    if (!this.o.backForward || this.busy || this.blocked()) return null;
    const s = this.span();
    if (!s || s.first !== s.last) return null;
    const entry = this.o.store.find(pageUrl(this.o.href(), s.last), this.o.section, this.o.now());
    if (
      !entry ||
      entry.through !== s.last ||
      !(entry.start >= 1) ||
      entry.start >= entry.through ||
      entry.through - entry.start + 1 > this.o.maxPages
    ) {
      return null;
    }
    const generation = this.generation;
    const pages = [];
    for (let p = entry.through - 1; p >= entry.start; p--) pages.push(p);
    const op = { dir: 'prev', page: pages[0], controller: new AbortController(), restoring: true };
    this.busy = op;
    const results = await Promise.all(
      pages.map((p) => {
        const cached = entry.pages?.[p];
        const source =
          typeof cached === 'string' ? Promise.resolve(cached) : this.o.fetchHtml(p, op.controller.signal, false);
        return source.then(
          (html) => ({ p, html }),
          () => ({ p, html: null })
        );
      })
    );
    // Only the operation that still owns the lock may release it (a filter change may have started another).
    if (generation !== this.generation || this.busy !== op) return null;
    this.busy = null;
    const b = this.bounds();
    for (const { p, html } of results) {
      const parsed = html ? this.o.view.parse(html) : null;
      if (!parsed) break; // keep the unbroken run next to the page on screen
      this.html.set(p, html);
      this.o.view.prepend(parsed, p, false);
      b.first = p;
    }
    this.afterChange(entry.anchor ?? null);
    if (entry.anchor) this.o.view.scrollToAnchor(entry.anchor);
    this.refresh();
    return entry;
  }

  /** A filter or sort change: whatever is in flight is dropped; nothing loads until the new grid has rendered. */
  beginReset() {
    this.generation++;
    this.busy?.controller.abort();
    this.busy = null;
    this.autoUsed = 0;
    this.failures = 0;
    this.range = null;
    this.html.clear();
    this.pendingReset = ++this.resetToken;
    return this.pendingReset;
  }

  /**
   * The render for a filter or sort change finished (rendered) or never arrived (not rendered). Only the latest
   * change counts. Not rendered: the cards on screen belong to the old URL, so nothing loads in place any more and
   * the control becomes a plain link that reloads the current URL.
   */
  endReset(token, rendered) {
    if (token !== this.pendingReset) return false;
    this.pendingReset = null;
    this.range = null;
    this.stale = !rendered;
    this.refresh();
    return true;
  }

  state() {
    const s = this.span();
    if (!s) return { mode: 'none' };
    if (this.stale) return { mode: 'list', shown: s.cards, total: this.o.view.total(), bottom: { mode: 'link', page: 1, label: 'continue' }, top: null };
    const next = this.nextPage();
    const prev = this.prevPage();
    const capped = this.capped();
    const failed = this.failures >= MAX_FAILURES;
    const b = this.bounds();
    let bottom;
    if (this.busy && this.busy.dir === 'next') bottom = { mode: 'loading', page: this.busy.page };
    else if (next === null) bottom = { mode: 'end', all: b.first === 1 };
    else if (capped) bottom = { mode: 'link', page: next, label: 'continue' };
    else if (failed) bottom = { mode: 'link', page: next, label: 'more', error: true };
    else bottom = { mode: 'more', page: next, error: this.failures > 0 };
    let top = null;
    if (prev !== null) {
      if (this.busy && this.busy.dir === 'prev' && !this.busy.restoring) top = { mode: 'loading', page: prev };
      else if (capped || failed) top = { mode: 'link', page: prev };
      else top = { mode: 'more', page: prev };
    }
    return { mode: 'list', shown: s.cards, total: this.o.view.total(), single: this.lastPage() <= 1, bottom, top };
  }

  refresh() {
    this.o.view.render(this.state());
  }
}

const SECTION_PREFIX = 'shopify-section-';
const CARD_SELECTOR = ':scope > [ref="cards[]"]';

/** Fills "[name]" placeholders in a translated template. */
export function fill(template, values) {
  return String(template || '').replace(/\[(\w+)\]/g, (whole, key) => (key in values ? String(values[key]) : whole));
}

const NUMBER = new Intl.NumberFormat('en-GB');

class BhlInfiniteScroll extends HTMLElement {
  /** @type {InfiniteController | null} */
  controller = null;
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** @type {Element | null} */
  #observed = null;
  /** @type {MutationObserver | null} */
  #mutations = null;
  #resetTimer = 0;
  #resetToken = 0;
  #syncQueued = false;

  connectedCallback() {
    const section = this.getAttribute('section-id') || '';
    if (!section || !this.#grid()) return;
    this.controller = new InfiniteController({
      view: this,
      section,
      fetchHtml: (page, signal, fresh) => abortable(
          sectionRenderer.getSectionHTML(section, !fresh, new URL(pageUrl(window.location.href, page, true)), signal),
          signal
        ),
      store: new SessionStore(safeSessionStorage()),
      autoPages: numberAttr(this, 'data-auto-pages', 2),
      maxPages: Math.max(2, numberAttr(this, 'data-max-pages', 8)),
      href: () => window.location.href,
      replaceHref: (url) => {
        try {
          history.replaceState(history.state, '', url);
        } catch {
          // a URL the browser refuses only leaves the address bar behind
        }
      },
      track: this.closest('collection-component') ? trackPageView : null,
      now: () => Date.now(),
      backForward: isBackForward(),
    });
    this.addEventListener('click', this.#onClick);
    document.addEventListener(StandardEvents.collectionUpdate, this.#onFilter);
    document.addEventListener(StandardEvents.searchUpdate, this.#onFilter);
    window.addEventListener('pagehide', this.#onHide);
    document.addEventListener('visibilitychange', this.#onVisibility);
    scrollContainerMediaQuery.addEventListener('change', this.#observe);
    this.#mutations = new MutationObserver(this.#onMutation);
    this.#mutations.observe(this, { childList: true, subtree: true });
    this.controller.refresh();
    this.#observe();
    this.#restore();
  }

  disconnectedCallback() {
    this.controller?.beginReset();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#onFilter);
    document.removeEventListener(StandardEvents.searchUpdate, this.#onFilter);
    window.removeEventListener('pagehide', this.#onHide);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    scrollContainerMediaQuery.removeEventListener('change', this.#observe);
    this.#io?.disconnect();
    this.#io = null;
    this.#observed = null;
    this.#mutations?.disconnect();
    clearTimeout(this.#resetTimer);
  }

  async #restore() {
    try {
      const entry = await this.controller?.restore();
      if (entry?.anchor) {
        let interacted = false;
        const mark = () => {
          interacted = true;
        };
        for (const type of ['wheel', 'touchstart', 'keydown', 'pointerdown']) {
          window.addEventListener(type, mark, { once: true, passive: true, capture: true });
        }
        // The theme and the browser restore the old scroll offset on pageshow; the card wins afterwards.
        const again = () =>
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              if (!interacted) this.scrollToAnchor(entry.anchor);
            })
          );
        if (document.readyState === 'complete') again();
        else window.addEventListener('pageshow', again, { once: true });
      }
    } catch {
      // the page as the server rendered it is a complete fallback
    } finally {
      this.removeAttribute('restoring');
    }
  }

  // ---------------------------------------------------------------- events

  #onClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target instanceof Element ? event.target : null;
    const control = target?.closest('[data-bhl-infinite-more], [data-bhl-infinite-prev]');
    if (!control || !this.controller) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return; // a new tab or window keeps the plain link
    }
    const dir = control.hasAttribute('data-bhl-infinite-prev') ? 'prev' : 'next';
    if (control.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }
    if (!this.controller.intercepts(dir)) return;
    event.preventDefault();
    this.controller.load(dir, 'click');
  };

  #onFilter = (/** @type {Event} */ event) => {
    const mine = this.closest('.shopify-section');
    const from = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
    if (from && mine && from !== mine) return;
    if (!this.controller) return;
    const token = this.controller.beginReset();
    this.#resetToken = token;
    clearTimeout(this.#resetTimer);
    this.#resetTimer = window.setTimeout(() => this.#endReset(token, false), RESET_TIMEOUT_MS);
    // Search & Discovery (assets/facets.js) passes the render's promise on the event; it settles after the morph.
    // Without one there is nothing to attribute a render to, so the wait ends by the timeout as not rendered.
    const promise = /** @type {any} */ (event).promise;
    if (promise && typeof promise.then === 'function') {
      promise.then(
        () => this.#verifyRender(token),
        () => this.#endReset(token, false)
      );
    }
  };

  /**
   * The render for this token settled. It counts as rendered only when the grid on screen is the grid the server
   * renders for the current URL (the renderer's cached answer for it, so normally no request): an older render that
   * landed late, or a render whose morph failed, leaves a different grid and ends the wait as not rendered.
   */
  async #verifyRender(token) {
    if (token !== this.#resetToken || !this.controller) return;
    let rendered = false;
    try {
      const section = this.getAttribute('section-id') || '';
      // The same URL facets.js rendered (the current location), so the renderer answers from its cache.
      const html = await sectionRenderer.getSectionHTML(section, true, new URL(window.location.href));
      const parsed = this.parse(html);
      rendered = !!parsed && parsed.hasGrid && this.#signatureOf(parsed.grid, parsed.cards) === this.#signature();
    } catch {
      rendered = false;
    }
    this.#endReset(token, rendered);
  }

  /** Only the latest filter or sort change ends the wait (older promises and timers are ignored). */
  #endReset(token, rendered) {
    if (token !== this.#resetToken || !this.controller) return;
    clearTimeout(this.#resetTimer);
    if (this.controller.endReset(token, rendered)) this.#observe();
  }

  #onMutation = () => {
    if (this.#syncQueued) return;
    this.#syncQueued = true;
    queueMicrotask(() => {
      this.#syncQueued = false;
      if (this.#region() !== this.#observed) this.#observe();
    });
  };

  #onHide = () => {
    if (window.Shopify?.designMode) return;
    this.controller?.save(this.anchor());
  };

  #onVisibility = () => {
    if (document.visibilityState === 'hidden') this.#onHide();
  };

  #observe = () => {
    this.#io?.disconnect();
    this.#io = null;
    this.#observed = null;
    const region = this.#region();
    if (!region || typeof IntersectionObserver === 'undefined') return;
    this.#io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) this.controller?.load('next', 'auto');
      },
      { root: getIntersectionRoot(), rootMargin: ROOT_MARGIN }
    );
    this.#io.observe(region);
    this.#observed = region;
  };

  // ---------------------------------------------------------------- view (used by the controller)

  #grid() {
    return this.querySelector('[ref="grid"]');
  }

  #region() {
    return this.querySelector('[data-bhl-infinite-region]');
  }

  #cards() {
    const grid = this.#grid();
    return grid ? /** @type {HTMLElement[]} */ ([...grid.querySelectorAll(CARD_SELECTOR)]) : [];
  }

  #signature() {
    return this.#signatureOf(this.#grid(), this.#cards());
  }

  /** What identifies a rendered grid: its last page and its product cards in order. */
  #signatureOf(grid, cards) {
    return `${grid?.getAttribute('data-last-page')}|${cards.map((c) => c.getAttribute('data-product-id') || c.id).join(',')}`;
  }

  span() {
    const cards = this.#cards();
    if (!cards.length) return null;
    const first = Number(cards[0].dataset.page) || 1;
    const last = Number(cards[cards.length - 1].dataset.page) || first;
    return { first, last, cards: cards.length };
  }

  lastPage() {
    return Number(this.#grid()?.getAttribute('data-last-page')) || 1;
  }

  setLastPage(page) {
    this.#grid()?.setAttribute('data-last-page', String(page));
  }

  total() {
    return Number(this.#region()?.getAttribute('data-total')) || 0;
  }

  /** @param {string} html */
  parse(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const id = SECTION_PREFIX + (this.getAttribute('section-id') || '');
    const section = doc.getElementById(id);
    if (!section) return null;
    const grid = section.querySelector('[ref="grid"]');
    return {
      grid,
      hasGrid: !!grid,
      cards: grid ? [...grid.querySelectorAll(CARD_SELECTOR)] : [],
      lastPage: grid ? Number(grid.getAttribute('data-last-page')) || null : null,
      others: [...section.querySelectorAll('.bhl-other-results__item')],
      otherSection: section.querySelector('.bhl-other-results'),
    };
  }

  #prepare(cards, page) {
    const grid = this.#grid();
    const seen = new Set(this.#cards().map((c) => c.dataset.productId));
    const fragment = document.createDocumentFragment();
    let first = null;
    let count = 0;
    for (const card of cards) {
      const id = card.getAttribute('data-product-id');
      if (id && seen.has(id)) continue; // the catalogue moved between pages: never the same product twice
      if (id) seen.add(id);
      card.setAttribute('data-page', String(page));
      for (const img of card.querySelectorAll('img[loading="eager"]')) img.setAttribute('loading', 'lazy');
      for (const img of card.querySelectorAll('img[fetchpriority="high"]')) img.removeAttribute('fetchpriority');
      const adopted = document.adoptNode(card);
      fragment.append(adopted);
      first ??= adopted;
      count++;
    }
    return { grid, fragment, first, count };
  }

  append(parsed, page) {
    const { grid, fragment, first, count } = this.#prepare(parsed.cards, page);
    grid?.append(fragment);
    this.#mergeOthers(parsed);
    return { first, count };
  }

  prepend(parsed, page, keepScroll) {
    const { grid, fragment, first, count } = this.#prepare(parsed.cards, page);
    const before = this.#cards()[0] ?? null;
    const top = before ? before.getBoundingClientRect().top : 0;
    if (grid) grid.insertBefore(fragment, before);
    if (keepScroll && before) {
      const delta = before.getBoundingClientRect().top - top;
      if (delta) scrollTo({ top: getScrollTop() + delta, behavior: 'instant' });
    }
    this.#mergeOthers(parsed);
    return { first, count };
  }

  /** Search only: pages and articles that matched on a loaded page join the list under the grid. */
  #mergeOthers(parsed) {
    if (!parsed.others.length) return;
    const section = this.closest('.shopify-section');
    const list = section?.querySelector('.bhl-other-results__list');
    if (list) {
      const hrefs = new Set([...list.querySelectorAll('a[href]')].map((a) => a.getAttribute('href')));
      for (const item of parsed.others) {
        const href = item.querySelector('a[href]')?.getAttribute('href');
        if (href && hrefs.has(href)) continue;
        if (href) hrefs.add(href);
        list.append(document.adoptNode(item));
      }
    } else if (parsed.otherSection) {
      this.closest('#ResultsList')?.after(document.adoptNode(parsed.otherSection));
    }
  }

  focusCard(card) {
    const link = card.querySelector('a[href]');
    if (link instanceof HTMLElement) link.focus();
    else {
      card.setAttribute('tabindex', '-1');
      card.focus();
    }
  }

  /** A load that added no new card (every product already shown): focus stays on a visible control. */
  focusStatus(dir) {
    const control = this.querySelector(dir === 'prev' ? '[data-bhl-infinite-prev]' : '[data-bhl-infinite-more]');
    const end = this.querySelector('[data-bhl-infinite-end]');
    if (control instanceof HTMLElement && !control.hidden && !control.closest('[hidden]')) control.focus();
    else if (dir === 'next' && end instanceof HTMLElement && !end.hidden) end.focus();
    else this.#cards()[0]?.querySelector('a[href]')?.focus();
  }

  actionFocused(dir) {
    const active = document.activeElement;
    return !!active && active.matches(dir === 'prev' ? '[data-bhl-infinite-prev]' : '[data-bhl-infinite-more]') && this.contains(active);
  }

  /** The card nearest the top of the view and its offset, for Back. */
  anchor() {
    for (const card of this.#cards()) {
      const rect = card.getBoundingClientRect();
      if (rect.bottom > 0) return { id: card.dataset.productId || '', top: Math.round(rect.top) };
    }
    return null;
  }

  scrollToAnchor(anchor) {
    if (!anchor?.id) return;
    const card = this.#cards().find((c) => c.dataset.productId === anchor.id);
    if (!card) return;
    const delta = card.getBoundingClientRect().top - anchor.top;
    if (delta) scrollTo({ top: getScrollTop() + delta, behavior: 'instant' });
  }

  recheck() {
    if (this.#io && this.#observed) {
      this.#io.unobserve(this.#observed);
      this.#io.observe(this.#observed);
    }
  }

  announce(kind, values) {
    const region = this.#region();
    const live = this.querySelector('[data-bhl-infinite-live]');
    if (!region || !live) return;
    let text = '';
    if (kind === 'error') text = region.getAttribute('data-text-error') || '';
    else if (kind === 'loaded') {
      if (!values.count) return; // nothing new on that page
      // Collections: positions from the page size (every result is a product). Search: the template uses only
      // [shown], because its page size and total also count pages and articles.
      const size = Number(region.getAttribute('data-page-size')) || values.count;
      const from = (values.page - 1) * size + 1;
      text = fill(region.getAttribute('data-text-loaded'), {
        from: NUMBER.format(from),
        to: NUMBER.format(from + values.count - 1),
        total: NUMBER.format(this.total()),
        shown: NUMBER.format(this.#cards().length),
      });
    }
    live.textContent = '';
    window.setTimeout(() => {
      live.textContent = text;
    }, 50);
  }

  render(state) {
    const region = this.#region();
    const previous = this.querySelector('[data-bhl-infinite-previous]');
    if (state.mode !== 'list') {
      if (region) region.hidden = true;
      if (previous instanceof HTMLElement) previous.hidden = true;
      return;
    }
    // Enhanced: the numbered pagination stays in the HTML (crawlable ?page=N links) but is not shown. Done here, not
    // in CSS, so it shows whenever the script is missing or fails; re-applied after every render of the section.
    for (const nav of this.querySelectorAll('.pagination')) {
      if (nav instanceof HTMLElement) nav.hidden = true;
    }
    if (region) {
      region.hidden = false;
      const progress = region.querySelector('[data-bhl-infinite-progress]');
      let more = region.querySelector('[data-bhl-infinite-more]');
      const end = region.querySelector('[data-bhl-infinite-end]');
      const b = state.bottom;
      const total = NUMBER.format(state.total);
      if (progress instanceof HTMLElement) {
        progress.hidden = !!state.single; // one page: the end message alone
        progress.textContent = b.error
          ? region.getAttribute('data-text-error') || ''
          : fill(region.getAttribute('data-text-progress'), { shown: NUMBER.format(state.shown), total });
      }
      if (!(more instanceof HTMLAnchorElement) && b.mode !== 'end') {
        // The server leaves it out on a last page; a later state (a failed filter render) can still need a link.
        more = document.createElement('a');
        more.className = 'button button-secondary bhl-infinite__button';
        more.setAttribute('data-bhl-infinite-more', '');
        region.querySelector('.bhl-infinite__action')?.prepend(more);
      }
      if (more instanceof HTMLAnchorElement) {
        const focused = document.activeElement === more;
        if (b.mode === 'end') {
          more.hidden = true;
          more.removeAttribute('aria-disabled');
          more.removeAttribute('aria-busy');
        } else {
          more.hidden = false;
          more.href = pageUrl(window.location.href, b.page);
          const busy = b.mode === 'loading';
          if (busy) more.setAttribute('aria-busy', 'true');
          else more.removeAttribute('aria-busy');
          if (busy) more.setAttribute('aria-disabled', 'true');
          else more.removeAttribute('aria-disabled');
          more.textContent =
            b.mode === 'loading'
              ? region.getAttribute('data-text-loading') || ''
              : b.label === 'continue'
                ? fill(region.getAttribute('data-text-continue'), { page: NUMBER.format(b.page) })
                : region.getAttribute('data-text-more') || '';
        }
        if (focused && more.hidden && end instanceof HTMLElement) {
          end.hidden = false;
          end.focus();
        }
      }
      if (end instanceof HTMLElement) {
        end.hidden = b.mode !== 'end';
        if (b.mode === 'end') {
          const key = b.all ? (state.shown === 1 ? 'data-text-end-one' : 'data-text-end-all') : 'data-text-end-last';
          end.textContent = fill(region.getAttribute(key), { total: b.all ? NUMBER.format(state.shown) : total });
        }
      }
    }
    if (previous instanceof HTMLElement) {
      const link = previous.querySelector('[data-bhl-infinite-prev]');
      const t = state.top;
      previous.hidden = !t;
      if (t && link instanceof HTMLAnchorElement) {
        link.href = pageUrl(window.location.href, t.page);
        const busy = t.mode === 'loading';
        if (busy) link.setAttribute('aria-busy', 'true');
        else link.removeAttribute('aria-busy');
        if (busy) link.setAttribute('aria-disabled', 'true');
        else link.removeAttribute('aria-disabled');
        link.textContent = busy
          ? previous.getAttribute('data-text-loading') || ''
          : previous.getAttribute('data-text-previous') || '';
      } else if (!t && link instanceof HTMLElement && document.activeElement === link) {
        this.#cards()[0]?.querySelector('a[href]')?.focus();
      }
    }
  }
}

/**
 * Lets this feature stop waiting on its own signal even when the shared renderer hands back a request it did not
 * start (Horizon's adjacent-page prefetch, which has no signal): requests started here carry the signal and are
 * aborted with it; a shared prefetch is left to Horizon and its answer is ignored.
 * @param {Promise<string>} promise
 * @param {AbortSignal} signal
 */
export function abortable(promise, signal) {
  return new Promise((resolve, reject) => {
    const abort = () => reject(new DOMException('Aborted', 'AbortError'));
    if (signal.aborted) return abort();
    signal.addEventListener('abort', abort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}

function numberAttr(el, name, fallback) {
  const value = Number(el.getAttribute(name));
  return Number.isFinite(value) && el.hasAttribute(name) && value >= 0 ? value : fallback;
}

function safeSessionStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isBackForward() {
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    return !!nav && /** @type {PerformanceNavigationTiming} */ (nav).type === 'back_forward';
  } catch {
    return false;
  }
}

/** The theme's own page view event (assets/page-view-event.js), once per page loaded into the list. */
function trackPageView(url) {
  try {
    const template = /** @type {HTMLElement | null} */ (document.querySelector('main[data-template]'))?.dataset.template || '';
    document.dispatchEvent(new PageViewEvent({ page: { template, title: document.title, url } }));
  } catch {
    // analytics never blocks the list
  }
}

if (!customElements.get('bhl-infinite-scroll')) {
  customElements.define('bhl-infinite-scroll', BhlInfiniteScroll);
}
